/**
 * Options of a list status select, fed by a `WS2_EA_SEL_*` endpoint
 * (`WS2_EA_SEL_STATUT_CONTRAT` / `_PRIME` / `_SINISTRE` — fiches 22986 /
 * 22988 / 22989): `{ value: VAL, label: LIB }` in API order.
 *
 * One cached query per VEOS environment and endpoint, kept for the SPA
 * session (the lists rarely change); disabled without an active environment
 * (admin preview, logged out) — the select then only offers its « Tous … »
 * option.
 */

import { useQuery } from '@tanstack/react-query';

import type { SelOption } from '../api/listFilters';
import { useExtranetAuthStore } from '../auth/store';

/** Status options rarely change: keep them for 30 minutes. */
export const SEL_OPTIONS_STALE_TIME_MS = 30 * 60_000;

const NO_OPTIONS: readonly SelOption[] = [];

/** Query key of a SEL option list — scoped by environment and endpoint. */
export function selOptionsQueryKey(
  registrySlug: string | null,
  endpointSlug: string,
): readonly unknown[] {
  return ['extranet', 'sel-options', registrySlug, endpointSlug];
}

/**
 * The options of `endpointSlug`, loaded with `fetchOptions` (the domain
 * service's fetcher); `[]` while loading, on error or without environment.
 */
export function useSelOptions(
  endpointSlug: string,
  fetchOptions: (registrySlug: string) => Promise<SelOption[]>,
): readonly SelOption[] {
  const registrySlug = useExtranetAuthStore((s) => s.registrySlug);
  const query = useQuery<SelOption[]>({
    queryKey: selOptionsQueryKey(registrySlug, endpointSlug),
    queryFn: () => fetchOptions(registrySlug ?? ''),
    enabled: Boolean(registrySlug),
    staleTime: SEL_OPTIONS_STALE_TIME_MS,
  });
  return query.data ?? NO_OPTIONS;
}
